// useMyProfile — fetches the signed-in user's profile row (username, display
// name, avatar, country) so we can deep-link to their public /profile/:username
// page from the navbar avatar dropdown and the Account page.
//
// Uses a module-level shared store so every consumer (navbar, account page,
// edit modal, etc.) sees the same value and re-renders together when one
// caller invokes refresh(). Without this, each useState copy would drift
// after an update — e.g. the modal would refresh its local profile but the
// Account page URL display would stay stale until remount.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

type MyProfile = {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  countryCode: string | null;
  bio: string | null;
  websiteUrl: string | null;
  twitterHandle: string | null;
  githubHandle: string | null;
  linkedinHandle: string | null;
};

// ---- Shared module-level store ---------------------------------------------
let cachedProfile: MyProfile | null = null;
let cachedUserId: string | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

async function loadProfile(userId: string, force = false): Promise<void> {
  if (!force && cachedUserId === userId && cachedProfile) return;
  if (inflight) return inflight;
  inflight = (async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select(
        "username, display_name, avatar_url, country_code, bio, website_url, twitter_handle, github_handle, linkedin_handle",
      )
      .eq("user_id", userId)
      .maybeSingle();
    cachedUserId = userId;
    if (error || !data) {
      cachedProfile = null;
    } else {
      cachedProfile = {
        username: data.username,
        displayName: data.display_name,
        avatarUrl: data.avatar_url,
        countryCode: data.country_code ?? null,
        bio: data.bio ?? null,
        websiteUrl: data.website_url ?? null,
        twitterHandle: data.twitter_handle ?? null,
        githubHandle: data.github_handle ?? null,
        linkedinHandle: data.linkedin_handle ?? null,
      };
    }
    notify();
  })();
  try {
    await inflight;
  } finally {
    inflight = null;
  }
}

function clearProfile() {
  cachedProfile = null;
  cachedUserId = null;
  notify();
}

export function useMyProfile() {
  const { user } = useAuth();
  const [, setTick] = useState(0);
  const [loading, setLoading] = useState(false);

  // Subscribe to shared store updates.
  useEffect(() => {
    const sub = () => setTick((n) => n + 1);
    listeners.add(sub);
    return () => {
      listeners.delete(sub);
    };
  }, []);

  // Fetch on mount / user change.
  useEffect(() => {
    if (!user) {
      clearProfile();
      return;
    }
    if (cachedUserId !== user.id) {
      // Different user signed in — drop stale cache before loading.
      cachedProfile = null;
      cachedUserId = null;
    }
    setLoading(true);
    loadProfile(user.id).finally(() => setLoading(false));
  }, [user]);

  const refresh = useCallback(async () => {
    if (!user) {
      clearProfile();
      return;
    }
    setLoading(true);
    try {
      await loadProfile(user.id, true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const profile = user && cachedUserId === user.id ? cachedProfile : null;
  return { profile, loading, refresh };
}
